import React, {useState, useEffect, useRef} from 'react';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { Block, Icon, FormGroup, useForm, Map, CoordinatesModal, Dropzone, CoordinatesImportModal } from '../components';
import { useTranslation } from 'react-i18next';
import { Container, Button, Col, Row, DropdownButton, ButtonGroup, Dropdown } from 'react-bootstrap';
import { genRanHex } from '../utils'
import useCustomTable from "../tables/useCustomTable";
import {TableSearchRow} from "../tables/TableSearch";
import BlockTable from "../BlockTable";
import Table from "../tables/Table";
import Pagination from "../tables/Pagination";

function CoordinatesFilterForm({onChange, value, disabled}) {
  const [showCoordinatesModal, setCoordinatesShowModal] = useState(false);
  const [showCoordinatesImportModal, setCoordinatesImportShowModal] = useState(false);
  const [modalKey, setModalKey] = useState(genRanHex())
  const [importModalKey, setImportModalKey] = useState(genRanHex())
  const [modalCoordinates, setModalCoordinates] = useState(null);
  const [tableRowHighlighted, setTableRowHighlighted] = useState(null);
  const [mapHovered, setMapHovered] = useState(null);
  const [file, setFile] = useState(null);
  // const [showReplaceAlert, setShowReplaceAlert] = useState(false)
  const addButtonRef = useRef(null);
  const {t} = useTranslation();

  const {formData, getInputProps} = useForm({
    defaultObject: value,
    initialValues: {
      radius: '',
      coordinates: []
    }
  })

  useEffect(() => {
    // Only notify the parent when our data actually differs from what it already
    // holds. The parent passes our own formData back as `value`, which useForm
    // re-adopts as defaultObject and rebuilds into a new object: echoing that
    // back unconditionally creates an infinite update loop
    // ("Maximum update depth exceeded" in ZoneForm).
    if (!isEqual(formData, value)) {
      onChange(formData)
    }
  }, [formData])

  const columns = [{
      Header: 'Id',
      accessor: 'id',
    }, {
      Header: 'Key',
      accessor: 'key',
    }, {
      Header: 'Latitude',
      accessor: 'latitude',
    }, {
      Header: 'Longitude',
      accessor: 'longitude',
    }, {
      Header: 'Radius',
      accessor: 'radius',
      Cell: (cell) => String(cell.value || formData.radius)
    }
  ]

  const tableOptions = {
    sortable: true,
    filterable: true,
    paginate: true
  }

  const handleAdd = (event) => {
    event.stopPropagation()
    setModalCoordinates(null)
    setCoordinatesShowModal(true)
  }

  const handleNewFile = file => {
    setFile(file)
    setCoordinatesImportShowModal(true)
  }

  const handleImport = () => {
    setFile(null)
    setCoordinatesImportShowModal(true)
  }

  const handleClose = () => {
    setCoordinatesShowModal(false)
    // addButtonRef.current.blur()
    setModalKey(genRanHex())
  }

  const handleImportModalClose = () => {
    setFile(false)
    setCoordinatesImportShowModal(false)
    setImportModalKey(genRanHex())
  }

  const handleSubmit = (coordinateFormData) => {
    processNewCoordinates([coordinateFormData])
    handleClose()
  }

  const handleImportSubmit = (coordinates) => {
    processNewCoordinates(coordinates)
    handleImportModalClose()
  }

  const processNewCoordinates = (coordinates = []) => {
    let newFormData = formData.coordinates.slice()
    coordinates.forEach(coordinate => {
      // Replace if key already exists - TO DO
      /*if (coordinate.key) {
        const index = newFormData.map(x=> x.key).indexOf(coordinate.key)
        if (index >= 0) {
          setShowReplaceAlert(true)
        }
      }*/
      if (coordinate.id) {
        const index = newFormData.map(x=> x.id).indexOf(coordinate.id)
        if (index >= 0) {
          if (coordinate.delete) {
            newFormData.splice(index, 1)
          } else {
            newFormData[index] = coordinate
          }
        } else {
          newFormData.push(coordinate)
        }
      } else {
        coordinate.id = genRanHex(3)
        newFormData.push(coordinate)
      }
    })
    getInputProps("coordinates").onChange(newFormData)
  }

  const customData = {
    type: 'FeatureCollection',
    properties: {type: 'point', interactive: true},
    features: formData.coordinates.map((coord) => ({
      type: 'Feature',
      properties: {id: coord.id, radius: coord.radius || formData.radius},
      geometry: {type: 'Point', coordinates: [parseFloat(coord.longitude), parseFloat(coord.latitude)]}
    }))
  }

  const handleMapHover = (feature) => {
    const index = feature ? formData.coordinates.map( x => x.id ).indexOf(feature.value) : null
    setTableRowHighlighted(index >= 0 ? index : null)
  }

  const handleRowHover = (row) => {
    if (row) {
      setMapHovered(row.id)
    } else {
      setMapHovered(null)
    }
  }

  const showCoordinates = (rowData) => {
    setModalCoordinates(rowData)
    setCoordinatesShowModal(true)
  }

  const {
    tableProps,
    paginationProps,
    tableSearchProps,
    tablePageLengthProps
  } = useCustomTable()

  const heightStyle = {
    height: '507px',
    marginBottom: '30px'
  }

  return (
    <>
      <Row>
        <Block as={Col} md={6} sm={12}>
          <FormGroup
            label={t("zone.coordinates.defaultRadius")}
            input="number"
            text={t("zone.coordinates.defaultRadiusText")}
            {...getInputProps("radius")}/>
        </Block>
        <Col>
          <DropdownButton
            as={ButtonGroup}
            variant="primary"
            title={t("add")}
            className="float-right"
            ref={addButtonRef}
          >
            <Dropdown.Item variant='primary' onClick={handleAdd}>
              <Icon variant="primary" icon='plus' text={t("add")}/>
            </Dropdown.Item>
            <Dropdown.Item variant='primary' onClick={handleImport}>
              <Icon variant="primary" icon='upload' text={t("import")}/>
            </Dropdown.Item>
          </DropdownButton>
        </Col>
      </Row>
      <TableSearchRow {...tableSearchProps}/>
      <Row>
        <Col md={12} lg={6}>
          {formData.coordinates && formData.coordinates.length > 0 ? <BlockTable style={{marginBottom: "15px"}}>
              <Table
                small
                data={formData.coordinates}
                columns={columns}
                hiddenColumns={['id']}
                {...tableOptions}
                {...tableProps}
                highlighted={tableRowHighlighted}
                onRowClick={showCoordinates}
                onRowHover={handleRowHover}/>
            </BlockTable> : <Placeholder
            onAdd={handleAdd}
            onDrop={handleNewFile}
          />}
            <Pagination {...paginationProps}/>
        </Col>
        <Col md={12} lg={6} style={heightStyle}>
          <Map
            zoomLevel={4}
            pointData={customData}
            onHover={handleMapHover}
            disabled={true}
            hovered={{value: mapHovered, sources: ['fill', 'point']}}
          />
        </Col>
      </Row>
      <CoordinatesModal
        key={modalKey}
        show={showCoordinatesModal}
        coordinates={modalCoordinates}
        handleClose={handleClose}
        handleSubmit={handleSubmit}
        radiusPlaceholder={formData.radius}/>
      <CoordinatesImportModal
        key={importModalKey}
        handleClose={handleImportModalClose}
        show={showCoordinatesImportModal}
        handleSubmit={handleImportSubmit}
        file={file}
      />
      {/*{showReplaceAlert && <SweetAlert
        warning
        title="Parsing error"
        confirmBtnBsStyle="light"
        onConfirm={handleModalConfirm}>
        {parsingErrors.message}}
      </SweetAlert>*/}
    </>
  )
}

export default CoordinatesFilterForm

CoordinatesFilterForm.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.shape({
    radius: PropTypes.string,
    coordinates: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        key: PropTypes.string.isRequired,
        latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        radius: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      })
    )
  })
}

function Placeholder({onAdd, onDrop}) {
  const {t} = useTranslation();
  return <Container style={{height: '100%'}}>
    <Dropzone
      as={Row}
      className="justify-content-center align-items-center"
      style={{height: "100%"}}
      multiple={false}
      onDrop={onDrop}
    >
      <div><Icon icon={"csv"} size={"4x"} variant={"primary"}/></div>
      <div style={{marginTop: '20px'}}>{t("zone.coordinates.noData")}</div>
      <Button style={{marginTop: '20px'}} variant={"primary"} onClick={onAdd}>{t("add")}</Button>
    </Dropzone>
  </Container>
}

Placeholder.propTypes = {}
